import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SignaturePad } from "@/components/SignaturePad";
import { ContractDocument } from "@/components/admin/ContractDocument";
import { getContractForEmployee, signContractAsEmployee } from "@/lib/contracts.functions";
import type { EmploymentContract } from "@/lib/contracts";

export const Route = createFileRoute("/contract/$token")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Employment contract | N S Physiotherapy" },
      { name: "description", content: "Review and sign your employment contract with N S Physiotherapy." },
      { property: "og:title", content: "Employment contract | N S Physiotherapy" },
      { property: "og:description", content: "Review and sign your employment contract." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: EmployeeContract,
});

function EmployeeContract() {
  const { token } = Route.useParams();
  const qc = useQueryClient();
  const getFn = useServerFn(getContractForEmployee);
  const signFn = useServerFn(signContractAsEmployee);
  const { data: c, isLoading } = useQuery({ queryKey: ["employee-contract", token], queryFn: () => getFn({ data: { token } }) as Promise<EmploymentContract | null> });
  const [name, setName] = useState("");
  const [sig, setSig] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  if (isLoading) return <main className="p-8 text-center text-sm text-muted-foreground">Loading contract…</main>;
  if (!c) return <main className="p-8 text-center"><h1 className="text-xl font-bold">Contract not available</h1><p className="text-sm text-muted-foreground">This link is invalid or the contract was cancelled. Please contact the practice.</p></main>;

  const download = () => import("@/lib/contract-pdf").then((m) => m.downloadContractPdf(c)).catch((e) => toast.error((e as Error).message));
  const sign = async () => {
    if (!sig || !agreed || name.trim().length < 2) return;
    setBusy(true);
    try { await signFn({ data: { token, signed_name: name, signature_png: sig, agreed: true } }); toast.success("Thank you — your contract is signed. A copy has been emailed to you."); qc.invalidateQueries({ queryKey: ["employee-contract", token] }); }
    catch (e) { toast.error((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <main className="mx-auto max-w-4xl space-y-6 px-4 py-8">
      {c.status === "signed" ? (
        <div className="rounded-md border bg-muted p-4 text-sm"><p className="font-semibold">This contract has been signed by both parties.</p><p className="text-muted-foreground">Download a copy for your records.</p><Button className="mt-3" onClick={download}>Download signed contract (PDF)</Button></div>
      ) : (
        <p className="text-sm text-muted-foreground">Please read your contract below, then sign at the bottom of the page.</p>
      )}
      <ContractDocument {...c} />
      {c.status === "awaiting_employee" && (
        <section className="mx-auto max-w-3xl space-y-4 rounded-md border bg-card p-6">
          <h2 className="text-lg font-bold">Sign your contract</h2>
          <div className="space-y-1"><Label htmlFor="n">Type your full name</Label><Input id="n" value={name} onChange={(e) => setName(e.target.value)} placeholder={c.employee_name} /></div>
          <div className="space-y-1"><Label>Draw your signature</Label><SignaturePad onChange={setSig} /></div>
          <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />I have read and understood this contract and agree to its terms. I understand my electronic signature is legally binding.</label>
          <Button className="w-full" disabled={!sig || !agreed || name.trim().length < 2 || busy} onClick={sign}>{busy ? "Signing…" : "Sign contract"}</Button>
        </section>
      )}
    </main>
  );
}
